import type { Root, Text } from "mdast";
import type { Plugin } from "unified";

import { $remark } from "@milkdown/kit/utils";
import { visit } from "unist-util-visit";

import { withMeta } from "../__internal__";

/**
 * Typography character mappings for Pandoc-style smart punctuation
 * Reserved for future use in bidirectional conversion
 */
const _TYPOGRAPHY_MAP = {
  parse: {
    "---": "—", // em-dash
    "--": "–", // en-dash
    "...": "…", // ellipsis
  },
  stringify: {
    "—": "---", // em-dash
    "–": "--", // en-dash
    "…": "...", // ellipsis
  },
};

/**
 * Transform text node values for parsing (ASCII → Unicode)
 */
function transformParseText(text: string): string {
  // Order matters: --- before -- to avoid partial matches
  return text
    .replace(/---/g, "—")
    .replace(/--/g, "–")
    .replace(/\.\.\./g, "…");
}

/**
 * Transform text for serialization (Unicode → ASCII)
 */
function transformStringifyText(text: string): string {
  return text.replace(/—/g, "---").replace(/–/g, "--").replace(/…/g, "...");
}

/**
 * A remark plugin to convert Pandoc typography sequences to Unicode characters
 * during parsing (Markdown → Editor):
 * - --- → — (em-dash)
 * - -- → – (en-dash)
 * - ... → … (ellipsis)
 */
const remarkTypographyParse: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, "text", (node: Text) => {
      node.value = transformParseText(node.value);
    });
  };
};

export const remarkTypographyParsePlugin = $remark(
  "remarkTypographyParse",
  () => remarkTypographyParse,
);

withMeta(remarkTypographyParsePlugin.plugin, {
  displayName: "Remark<remarkTypographyParse>",
  group: "Typography",
});

withMeta(remarkTypographyParsePlugin.options, {
  displayName: "RemarkConfig<remarkTypographyParse>",
  group: "Typography",
});

/**
 * mdast-util-to-markdown State interface (partial)
 */
interface ToMarkdownState {
  safe: (value: string, options: { before: string; after: string }) => string;
  enter: (name: string) => () => void;
  indexStack: number[];
  stack: unknown[];
}

/**
 * mdast-util-to-markdown Info interface (partial)
 */
interface ToMarkdownInfo {
  before: string;
  after: string;
}

/**
 * A remark plugin that registers text handlers for mdast-util-to-markdown
 * to convert Unicode typography characters back to Pandoc ASCII sequences
 * during serialization (Editor → Markdown):
 * - — → ---
 * - – → --
 * - … → ...
 */
const remarkTypographyStringify: Plugin<[], Root> = function () {
  const data = this.data() as {
    toMarkdownExtensions?: Array<{
      unsafe?: Array<{ character: string; inConstruct?: string[] }>;
      handlers?: Record<
        string,
        (
          node: Text,
          parent: unknown,
          state: ToMarkdownState,
          info: ToMarkdownInfo,
        ) => string
      >;
    }>;
  };

  // Add a custom handler for text nodes that transforms typography
  data.toMarkdownExtensions = data.toMarkdownExtensions || [];
  data.toMarkdownExtensions.push({
    handlers: {
      // Override the text handler to transform typography characters
      // while preserving proper escaping via state.safe()
      text: (
        node: Text,
        _parent: unknown,
        state: ToMarkdownState,
        info: ToMarkdownInfo,
      ) => {
        // First transform typography characters
        const transformed = transformStringifyText(node.value);
        // Then apply proper escaping
        return state.safe(transformed, {
          before: info.before,
          after: info.after,
        });
      },
    },
  });

  // Return identity transformer (the actual work is done by the handler)
  return (tree) => tree;
};

export const remarkTypographyStringifyPlugin = $remark(
  "remarkTypographyStringify",
  () => remarkTypographyStringify,
);

withMeta(remarkTypographyStringifyPlugin.plugin, {
  displayName: "Remark<remarkTypographyStringify>",
  group: "Typography",
});

withMeta(remarkTypographyStringifyPlugin.options, {
  displayName: "RemarkConfig<remarkTypographyStringify>",
  group: "Typography",
});
